import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Check, ArrowRight, ArrowLeft, Eye } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GoldButton, GhostButton, PhotoImg } from "../components/common";
import { ChipMultiSelect } from "../components/ChipMultiSelect";
import { HeightInput } from "../components/HeightInput";
import { AIAssistButton } from "../components/AIAssist";
import { ProfilePreview } from "../components/ProfilePreview";

const inputCls = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-4 py-2.5 text-sm text-[#FBF8F3] placeholder:text-[#9E978E]";
const STEPS = ["Basic", "Preferences", "About", "Interests", "Review", "Submit"];
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [p, setP] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [markets, setMarkets] = useState([]);
  const [options, setOptions] = useState({ dating_intentions: [], date_activities: [], interests: [], hobbies: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => { api.get("/markets").then((r) => setMarkets(r.data.markets || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/profile-options").then((r) => setOptions(r.data || {})).catch(() => {}); }, []);

  const load = async () => {
    const { data } = await api.get("/profile/me");
    if (data.onboarding_completed) { navigate("/me", { replace: true }); return; }
    setP(data);
    setForm({
      display_name: data.display_name || user?.display_name || "", gender: data.gender || "",
      market_id: data.market_id || "", occupation: data.occupation || "",
      height_cm: data.height_cm || "", preferred_offer: data.preferred_offer || "",
      headline: data.headline || "", about_me: data.about_me || "",
      primary_intention: data.primary_intention || "", dating_intentions: data.dating_intentions || [],
      date_preferences: data.date_preferences || [], interests: data.interests || [],
      hobbies: data.hobbies || [], languages: (data.languages || []).join(", "),
    });
    setStep(Math.min(Number(data.onboarding_step || 0), STEPS.length - 1));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const payload = (extra = {}) => ({
    display_name: form.display_name, gender: form.gender || null, market_id: form.market_id || "",
    occupation: form.occupation || null, height_cm: form.height_cm ? Number(form.height_cm) : null,
    preferred_offer: form.preferred_offer ? Number(form.preferred_offer) : null,
    headline: form.headline || null, about_me: form.about_me || null,
    primary_intention: form.primary_intention || null, dating_intentions: form.dating_intentions || [],
    date_preferences: form.date_preferences || [], interests: form.interests || [],
    hobbies: form.hobbies || [], languages: toArr(form.languages), ...extra,
  });

  const saveProgress = async (nextStep) => {
    setBusy(true);
    try {
      await api.put("/profile/me", payload({ onboarding_step: nextStep }));
      await refresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); return false; }
    finally { setBusy(false); }
  };

  const next = async () => {
    if (step === 0 && !form.display_name.trim()) return toast.error("Please add your display name");
    if (step === 0 && !form.market_id) return toast.error("Please select your DateBid market so others can find you");
    if (step === 1 && !form.primary_intention) return toast.error("Please choose your primary dating intention");
    const ns = Math.min(step + 1, STEPS.length - 1);
    if (await saveProgress(ns)) setStep(ns);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setBusy(true);
    try {
      await api.put("/profile/me", payload({ onboarding_step: STEPS.length - 1, onboarding_completed: true }));
      await refresh();
      toast.success("Profile submitted! An admin will review it shortly.");
      navigate("/me", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) return toast.error("Please upload a JPG, PNG or WEBP image.");
    if (file.size > 10 * 1024 * 1024) return toast.error("Max image size is 10MB.");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/photos?is_private=false`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success("Photo added");
      const { data } = await api.get("/profile/me"); setP(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed — please retry."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!p) return <div className="min-h-screen flex items-center justify-center text-[#9E978E]">Loading…</div>;
  const marketLabel = markets.find((m) => m.market_id === form.market_id)?.display_name;

  return (
    <div className="min-h-screen db-grain" style={{ background: "radial-gradient(1000px 500px at 80% -10%, rgba(117,26,50,0.2), transparent 60%), #0C0A0A" }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-6">
          <div className="font-serif text-2xl text-[#FBF8F3]">Welcome to Date<span className="text-[#D4AF37]">Bid</span></div>
          <p className="text-sm text-[#9E978E] mt-1">Let's build a profile that stands out. Your progress saves automatically.</p>
        </div>

        <div className="flex items-center justify-between mb-6 overflow-x-auto pb-1" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center shrink-0">
              <div className={`flex items-center gap-1.5 px-2 ${i === step ? "text-[#D4AF37]" : i < step ? "text-[#6FD79E]" : "text-[#9E978E]"}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${i === step ? "border-[#D4AF37] bg-[#541224]" : i < step ? "border-[#2E7D52] bg-[#2E7D52]/20" : "border-white/15"}`}>
                  {i < step ? <Check size={12} /> : i + 1}
                </span>
                <span className="text-[11px] font-medium hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-4 sm:w-6 h-px ${i < step ? "bg-[#2E7D52]" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#141113]/70 p-5 sm:p-6 space-y-5">
          {step === 0 && (
            <>
              <h2 className="font-serif text-xl text-[#FBF8F3]">Basic Information</h2>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Photos <span className="text-[#9E978E]">(a main photo helps a lot)</span></label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {(p.photos || []).map((ph) => (
                    <div key={ph.photo_id} className="relative aspect-[3/4] rounded-lg overflow-hidden border border-white/10">
                      <PhotoImg photoId={ph.photo_id} className="w-full h-full object-cover" />
                      {p.main_photo_id === ph.photo_id && <span className="absolute top-1 left-1 bg-[#D4AF37] text-[#0C0A0A] text-[8px] font-bold rounded px-1">MAIN</span>}
                    </div>
                  ))}
                  <button data-testid="onboarding-add-photo" onClick={() => fileRef.current.click()} disabled={uploading}
                    className="aspect-[3/4] rounded-lg border-2 border-dashed border-[#D4AF37]/30 flex flex-col items-center justify-center text-[#D4AF37] hover:border-[#D4AF37]/60 gap-1">
                    <Upload size={18} /><span className="text-[10px]">{uploading ? "…" : "Add"}</span>
                  </button>
                </div>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Display Name <span className="text-[#D4AF37]">*</span></label><input data-testid="onboarding-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className={inputCls} /></div>
                <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Gender</label><select data-testid="onboarding-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inputCls}><option value="">Select</option><option>Female</option><option>Male</option><option>Non-binary</option></select></div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">DateBid Market <span className="text-[#D4AF37]">* (your public location for discovery)</span></label>
                <select data-testid="onboarding-market" value={form.market_id} onChange={(e) => setForm({ ...form, market_id: e.target.value })} className={inputCls}>
                  <option value="">— Select your market —</option>
                  {markets.map((m) => <option key={m.market_id} value={m.market_id}>{m.display_name}{m.region ? ` · ${m.region}` : ""}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Occupation <span className="text-[#9E978E]">(optional)</span></label><input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className={inputCls} /></div>
                <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Preferred Offer ($) <span className="text-[#9E978E]">(optional)</span></label><input type="number" value={form.preferred_offer} onChange={(e) => setForm({ ...form, preferred_offer: e.target.value })} className={inputCls} /></div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Height <span className="text-[#9E978E]">(optional)</span></label>
                <HeightInput testid="onboarding-height" valueCm={form.height_cm ? Number(form.height_cm) : ""} onChange={(cm) => setForm({ ...form, height_cm: cm || "" })} />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="font-serif text-xl text-[#FBF8F3]">Dating Intentions & Ideal Date</h2>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Primary Intention <span className="text-[#D4AF37]">*</span></label>
                <p className="text-[11px] text-[#9E978E] mb-2">Tell members what kind of connection you're mainly looking for.</p>
                <ChipMultiSelect testid="onb-primary-intention" singleSelect options={options.dating_intentions || []} value={form.primary_intention ? [form.primary_intention] : []} onChange={(v) => setForm({ ...form, primary_intention: v[0] || "" })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Also Interested In <span className="text-[#9E978E]">(optional)</span></label>
                <ChipMultiSelect testid="onb-dating-intentions" hint="You can select more than one." options={options.dating_intentions || []} value={form.dating_intentions || []} onChange={(v) => setForm({ ...form, dating_intentions: v })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Ideal Date Activities <span className="text-[#9E978E]">(optional)</span></label>
                <ChipMultiSelect testid="onb-date-activities" hint="Pick what you'd enjoy — the actual plan is decided with your date later." options={options.date_activities || []} value={form.date_preferences || []} onChange={(v) => setForm({ ...form, date_preferences: v })} />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-serif text-xl text-[#FBF8F3]">Headline & About Me</h2>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Headline</label>
                  <AIAssistButton testid="onb-ai-headline" endpoint="/profile/ai/headline" draft={form.headline} label="Create with AI" onAccept={(t) => setForm((f) => ({ ...f, headline: t }))} />
                </div>
                <input data-testid="onboarding-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={inputCls} placeholder="A short statement that catches attention" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">About Me</label>
                  <AIAssistButton testid="onb-ai-about" endpoint="/profile/ai/about" draft={form.about_me} multiline label="Improve with AI" onAccept={(t) => setForm((f) => ({ ...f, about_me: t }))} />
                </div>
                <textarea data-testid="onboarding-about" rows={5} value={form.about_me} onChange={(e) => setForm({ ...form, about_me: e.target.value })} className={inputCls + " resize-none"} placeholder="Tell members about your personality, lifestyle and what makes you unique." />
                <p className="text-[11px] text-[#9E978E] mt-1">AI only polishes what you provide — it never invents facts, and you approve before it applies.</p>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-serif text-xl text-[#FBF8F3]">Interests & Hobbies</h2>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Interests</label>
                <ChipMultiSelect testid="onb-interests" hint="Select the things you genuinely enjoy." options={options.interests || []} value={form.interests || []} onChange={(v) => setForm({ ...form, interests: v })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Hobbies</label>
                <ChipMultiSelect testid="onb-hobbies" hint="What do you enjoy doing in your free time?" options={options.hobbies || []} value={form.hobbies || []} onChange={(v) => setForm({ ...form, hobbies: v })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Languages <span className="text-[#9E978E]">(comma separated, optional)</span></label>
                <input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className={inputCls} placeholder="English, French" />
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="font-serif text-xl text-[#FBF8F3]">Review Your Profile</h2>
              <p className="text-sm text-[#9E978E]">Here's a quick summary. Open the full preview to see exactly how members will see you.</p>
              <div className="rounded-xl border border-white/10 bg-[#0C0A0A]/40 p-4 text-sm text-[#D8D2C9] space-y-1.5">
                <div><span className="text-[#9E978E]">Name:</span> {form.display_name} {marketLabel ? `· ${marketLabel}` : ""}</div>
                {form.headline && <div className="text-[#C9A96E] italic">"{form.headline}"</div>}
                {form.primary_intention && <div><span className="text-[#9E978E]">Looking for:</span> {form.primary_intention}</div>}
                <div><span className="text-[#9E978E]">Activities:</span> {(form.date_preferences || []).join(", ") || "—"}</div>
                <div><span className="text-[#9E978E]">Interests:</span> {(form.interests || []).join(", ") || "—"}</div>
                <div><span className="text-[#9E978E]">Hobbies:</span> {(form.hobbies || []).join(", ") || "—"}</div>
              </div>
              <GhostButton data-testid="onboarding-open-preview" onClick={() => setPreview(true)} className="w-full flex items-center justify-center gap-2"><Eye size={15} /> Open Full Preview</GhostButton>
            </>
          )}

          {step === 5 && (
            <>
              <h2 className="font-serif text-xl text-[#FBF8F3]">Submit for Approval</h2>
              <p className="text-sm text-[#D8D2C9]">Your profile is ready! Submitting sends it to our team for a quick review. You can keep editing anytime from <span className="text-[#D4AF37]">My Profile</span>.</p>
              <div className="rounded-xl border border-[#D4AF37]/20 bg-[#541224]/15 p-4 text-[11px] text-[#9E978E] leading-relaxed">
                Reminder: The Date Offer amount is a private arrangement between you and your date. DateBid never collects, holds, or transfers that money. DateBid <span className="text-[#C9A96E]">credits</span> are a separate platform currency used only for app features like sending offers.
              </div>
              <GoldButton data-testid="onboarding-submit" onClick={submit} disabled={busy} className="w-full py-3">{busy ? "Submitting…" : "Submit Profile for Approval"}</GoldButton>
            </>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-white/8">
            <button data-testid="onboarding-back" onClick={back} disabled={step === 0 || busy}
              className="inline-flex items-center gap-1.5 text-sm text-[#9E978E] hover:text-[#FBF8F3] disabled:opacity-30"><ArrowLeft size={15} /> Back</button>
            <button data-testid="onboarding-skip" onClick={() => navigate("/me")} className="text-xs text-[#9E978E] hover:text-[#FBF8F3] underline underline-offset-2">Finish later</button>
            {step < STEPS.length - 1 && (
              <GoldButton data-testid="onboarding-next" onClick={next} disabled={busy} className="inline-flex items-center gap-1.5">{busy ? "Saving…" : "Next"} <ArrowRight size={15} /></GoldButton>
            )}
          </div>
        </div>
      </div>

      {preview && (
        <ProfilePreview form={form} photos={p.photos || []} mainPhotoId={p.main_photo_id}
          marketLabel={marketLabel} age={p.age} selfieVerified={p.selfie_verified} idVerified={p.id_verified}
          onClose={() => setPreview(false)} />
      )}
    </div>
  );
}
